import fleetlogicAPI from "helpers/fleetlogicAPI";
import i18n from "i18n/i18n";
import Uri from "jsuri";
import moment, { Moment } from "moment";
import React from "react";

import { Alert, DatePicker, Grid, Icons, Modal } from "@texada/ui";
import {
  CREATE_DATA_MANAGEMENT_URL,
  DATA_MANAGEMENT_TASKS
} from "constants/AppConstants";
import { getApiErrorMsg } from "helpers/helper";
import { useUser } from "helpers/hooks";
import { SRM_DATE_FORMAT } from "helpers/timeHelpers";

interface IContractArchiveModal {
  isOpen: boolean;
  onCancel: () => void | Promise<void>;
}

const getDefaultDate = () => moment().subtract(2, "y");

export const ContractArchiveModal: React.FC<IContractArchiveModal> = ({
  isOpen,
  onCancel
}) => {
  const [date, setDate] = React.useState<Moment>(getDefaultDate());
  const [loadingContractArchive, setLoadingContractArchive] =
    React.useState(false);
  const [error, setError] = React.useState("");
  const user = useUser();

  const handleCancel = () => {
    if (onCancel) onCancel();
    setDate(getDefaultDate());
  };

  const handleSubmit = async () => {
    if (!loadingContractArchive) {
      try {
        setLoadingContractArchive(true);
        const cutoff = moment.utc(date).local().format(SRM_DATE_FORMAT);
        const url = new Uri(`${CREATE_DATA_MANAGEMENT_URL}/${user?.srm_uid}`);
        await fleetlogicAPI.post(url.toString(), {
          cutoff,
          logging: 0,
          task: DATA_MANAGEMENT_TASKS.ARCHIVE_CONTRACT
        });
        Alert.success({
          message: i18n.t("the_archive_is_scheduled_and_will_complete_later")
        });
        setLoadingContractArchive(false);
        handleCancel();
      } catch (error) {
        setError(getApiErrorMsg(error));
        setLoadingContractArchive(false);
      }
    }
  };

  const handleChange = date => {
    setDate(date);
  };

  return (
    <Modal
      size={"xs"}
      isOpen={isOpen}
      header={i18n.t("archive_contracts")}
      onCancel={handleCancel}
      isLoading={loadingContractArchive}
      isSubmitting={loadingContractArchive}
    >
      <Modal.Body padded={false}>
        <Grid.Row>
          <Grid.Col>
            <DatePicker
              label={i18n.t("archive_contracts_older_than")}
              date={date}
              onChange={handleChange}
              maxDate={getDefaultDate()}
            />
            <div
              className="invalid-feedback"
              children={i18n.t(
                "we_recommend_utilizing_the_archive_feature_outside_of_regular_business_hours"
              )}
            />
            {error && <div className="invalid-feedback" children={error} />}
          </Grid.Col>
        </Grid.Row>
      </Modal.Body>
      <Modal.Footer
        saveButton
        saveButtonText={i18n.t("archive")}
        closeButton
        onSubmit={handleSubmit}
        saveButtonProps={{
          icon: { type: "left", render: () => <Icons.Archive /> }
        }}
      />
    </Modal>
  );
};
